import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Accordion,
} from "react-bootstrap";
import {
  UserX,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
} from "lucide-react";
import {
  fetchAbsentTeachers,
  fetchTeacherPeriods,
  fetchAlternativeTeachers,
  markTeacherAbsent,
  updateSubstituteAssignment,
  fetchSubstituteSheet,
  clearTeacherPeriods,
  clearAlternativeTeachers,
} from "./redux/substituteTeacherSlice";
import { fetchTeachers } from "../teachers/redux/teacherSlice";
import PrintDataTable from "../../../GuiComponents/PrintDataTable";

import ReactToPrint from "react-to-print";
import { getMasterDataByUserId } from "../../../pages/masterData/redux/masterDataThunk";
import { useLocation, useNavigate } from "react-router-dom";
import { startTour } from "../../../redux/globalJoyrideSlice";
import { substituteTeacherSteps } from "../../../tour/timetableTourSteps";

const SubstituteManagement = () => {
  const dispatch = useDispatch();
  const {
    absentTeachers,
    teacherPeriods,
    alternativeTeachers,
    substituteSheet,
    loading,
    error,
  } = useSelector((state) => state.substitutes);
  const { teachers } = useSelector((state) => state.teachers);
  const { user } = useSelector((state) => state.authSlice);
  const schoolId = useSelector((state) => state.masterData.school_id);

  const [showModal, setShowModal] = useState(false);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedPeriodForEdit, setSelectedPeriodForEdit] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null); // 'same' or 'different'
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [printTeacher, setPrintTeacher] = useState(null); // null => print whole sheet

  const printRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();


  const [formData, setFormData] = useState({
    teacher_id: [], // array of teacher id strings
    date: new Date().toISOString().split("T")[0],
    periods: [], // { teacher_id, period_id, subject_id, section_id }
  });

  // === Tour Start Logic ===
  useEffect(() => {
    if (location.state?.startSubstituteTeacherTour) {
      dispatch(
        startTour({
          name: "substituteTeacher",
          steps: substituteTeacherSteps(),
        })
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, dispatch, navigate, location.pathname]);

  // === Helpers ===
  function formatToDateOnly(isoString) {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function formatTimeRange(timeStr) {
    return (timeStr || "").slice(0, 5);
  }

  // Fetch periods for ALL selected teachers whenever teacher selection or date changes
  useEffect(() => {
    if (!schoolId) return;

    if (!formData.teacher_id || formData.teacher_id.length === 0) {
      dispatch(clearTeacherPeriods());
      setFormData((prev) => ({ ...prev, periods: [] }));
      return;
    }

    // Clear once then fetch each teacher's periods
    dispatch(clearTeacherPeriods());
    formData.teacher_id.forEach((tid) => {
      dispatch(
        fetchTeacherPeriods({
          teacherId: tid,
          date: formData.date,
          school_id: schoolId,
        })
      );
    });
    // Note: join used so dependency detects array content changes
  }, [formData.teacher_id.join(","), formData.date, schoolId, dispatch]);

  // --- Fetch master data, teachers, absent list, substitute sheet
  useEffect(() => {
    const fetchAll = async () => {
      try {
        if (user?.id && !schoolId) {
          await dispatch(getMasterDataByUserId(user.id)).unwrap();
        }
        if (schoolId && selectedDate) {
          await dispatch(fetchAbsentTeachers({ date: selectedDate, school_id: schoolId })).unwrap();
          await dispatch(fetchTeachers()).unwrap();
          await dispatch(fetchSubstituteSheet({ date: selectedDate, school_id: schoolId })).unwrap();
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    if (user?.id && schoolId) fetchAll();
  }, [dispatch, user?.id, schoolId, selectedDate]);

  // === Build teacher name on periods returned from store
  const periodsWithTeacherNames = useMemo(() => {
    if (!Array.isArray(teacherPeriods)) return [];
    return teacherPeriods.map((p) => {
      const t = teachers.find((tt) => String(tt.teacher_id) === String(p.teacher_id));
      return { ...p, teacher_name: t ? t.name : `Teacher ${p.teacher_id}` };
    });
  }, [teacherPeriods, teachers]);

  // === Group periods by teacher for the UI (order matches selected teachers)
  const groupedPeriods = useMemo(() => {
    const grouped = {};
    periodsWithTeacherNames.forEach((p) => {
      const key = String(p.teacher_id);
      if (!grouped[key]) grouped[key] = { teacher_id: p.teacher_id, teacher_name: p.teacher_name, periods: [] };
      grouped[key].periods.push(p);
    });
    return formData.teacher_id.map((tid) => grouped[String(tid)]).filter(Boolean);
  }, [periodsWithTeacherNames, formData.teacher_id]);

  // === Handlers ===
  const toggleTeacherCheckbox = (teacherId, checked) => {
    const idStr = String(teacherId);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        teacher_id: [...prev.teacher_id, idStr],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        teacher_id: prev.teacher_id.filter((id) => String(id) !== idStr),
        periods: prev.periods.filter((p) => String(p.teacher_id) !== String(teacherId)),
      }));
    }
  };

  const handleMarkAbsent = async (e) => {
    e.preventDefault();

    if (!user?.id || !schoolId) {
      alert("User ID or School ID is missing. Please try logging in again.");
      return;
    }

    if (!formData.teacher_id || formData.teacher_id.length === 0) {
      alert("Please select at least one teacher.");
      return;
    }

    if (!formData.periods || formData.periods.length === 0) {
      alert("Please select at least one period for the absent teacher(s).");
      return;
    }

    try {
      // Build payload without reason
      const payload = {
        teacher_id: formData.teacher_id.map((id) => Number(id)),
        date: formData.date,
        periods: formData.periods,
        user_id: user.id,
        school_id: schoolId,
      };

      await dispatch(markTeacherAbsent(payload)).unwrap();

      setShowModal(false);
      setFormData({
        teacher_id: [],
        date: new Date().toISOString().split("T")[0],
        periods: [],
      });

      dispatch(clearTeacherPeriods());
      dispatch(fetchAbsentTeachers({ date: selectedDate, school_id: schoolId }));
      dispatch(fetchSubstituteSheet({ date: selectedDate, school_id: schoolId }));

      alert("Teacher(s) marked absent and substitutes auto-assigned!");
    } catch (err) {
      console.error("Error marking teacher absent:", err);
      alert("Error marking teacher absent. Please try again.");
    }
  };

  const handleEditSubstitute = (absentTeacher, period) => {
    if (!schoolId) {
      alert("School ID is missing. Cannot fetch alternative teachers.");
      return;
    }

    setSelectedPeriodForEdit({
      absentTeacherId: absentTeacher.teacher_id,
      date: absentTeacher.date,
      periodId: period.period_id,
      subjectId: period.subject_id,
      currentSubstituteId: period.substitute_teacher_id,
      sectionId: period.section_id,
    });

    dispatch(
      fetchAlternativeTeachers({
        date: absentTeacher.date,
        periodId: period.period_id,
        subjectId: period.subject_id,
        absentTeacherId: absentTeacher.teacher_id,
        sectionId: period.section_id,
        school_id: schoolId,
      })
    );

    setShowAlternativesModal(true);
  };

  const handleUpdateSubstitute = async (newSubstituteTeacherId) => {
    if (!user?.id || !schoolId) {
      alert("User ID or School ID is missing. Please try logging in again.");
      return;
    }

    try {
      await dispatch(
        updateSubstituteAssignment({
          absentTeacherId: selectedPeriodForEdit.absentTeacherId,
          date: formatToDateOnly(selectedPeriodForEdit.date),
          periodId: selectedPeriodForEdit.periodId,
          newSubstituteTeacherId: parseInt(newSubstituteTeacherId),
          user_id: user.id,
          school_id: schoolId,
        })
      ).unwrap();

      setShowAlternativesModal(false);
      setSelectedPeriodForEdit(null);
      dispatch(clearAlternativeTeachers());
      dispatch(fetchAbsentTeachers({ date: selectedDate, school_id: schoolId }));
      dispatch(fetchSubstituteSheet({ date: selectedDate, school_id: schoolId }));
      setSelectedTeacherId("");
      alert("Substitute teacher updated successfully!");
    } catch (err) {
      console.error("Error updating substitute:", err);
      alert("Error updating substitute. Please try again.");
    }
  };

  const handlePrintReportModal = () => {
    setPrintTeacher(null); // ensure full sheet
    setShowPrintModal(true);
  };

  const getSubjectMatchBadge = (canTeachSubject) => {
    return canTeachSubject ? (
      <Badge bg="success" className="ms-2">Same Subject</Badge>
    ) : (
      <Badge bg="warning" className="ms-2">Different Subject</Badge>
    );
  };

  // Render UI
  return (
    <div className="px-5 ">
      <div className="px-2">
        <Row className="py-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center substitute-teacher-step">
              <div className="d-flex gap-2">
                <Button variant="danger" onClick={() => setShowModal(true)} className="d-flex align-items-center">
                  <UserX size={20} className="me-2" /> Mark Absent
                </Button>

                <Button variant="success" onClick={handlePrintReportModal} className="d-flex align-items-center">
                  <FileText size={20} className="me-2" /> Print Sheet
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

        <Row className="mb-4">
          <Col md={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0 d-flex align-items-center">
                  <Calendar size={24} className="me-2" /> Select Date
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  size="lg"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header style={{
            background: "linear-gradient(to right, #7B2FF7 50%, #F40076 100%)",
            fontSize: "0.85rem",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            color: "white"
          }}>
            <h5 className="mb-0">Substitute Assignments - {selectedDate}</h5>
          </Card.Header>

          <Card.Body>
            {loading ? (
              <div className="text-center py-4"><div className="loading-spinner" /></div>
            ) : (
              <Accordion>
                {substituteSheet.map((absent, index) => (
                  <Accordion.Item key={`${absent.teacher_id}-${absent.date}`} eventKey={index.toString()}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div><strong>{absent.teacher_name}</strong></div>

                        <div className="d-flex gap-2">
                          <Button className="p-2" size="sm" variant="success" onClick={(e) => { e.stopPropagation(); setPrintTeacher(absent); setShowPrintModal(true); }}>
                           <FileText size={24} className="" />  
                          </Button>
                        </div>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body>
                      {/* <div className="mb-3"><strong>Reason:</strong> {absent.reason || "—"}</div> */}

                      <Table responsive size="sm">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th>Time</th>
                            <th>Subject</th>
                            <th>Section</th>
                            <th>Substitute Teacher</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {absent.periods.map((period, periodIndex) => (
                            <tr key={periodIndex}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Clock size={16} className="me-2 text-primary" />
                                  {period.period_name}
                                </div>
                              </td>
                              <td>{formatTimeRange(period.start_time)} - {formatTimeRange(period.end_time)}</td>
                              <td>{period.subject_name}</td>
                              <td>{period.section_name}</td>
                              <td>
                                {period.substitute_teacher_name ? (
                                  <div className="d-flex align-items-center">
                                    <CheckCircle size={16} className="text-success me-2" /> {period.substitute_teacher_name}
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center">
                                    <XCircle size={16} className="text-danger me-2" />
                                    <span className="text-muted">Not Assigned</span>
                                  </div>
                                )}
                              </td>

                              <td>
                                <Button variant="outline-primary" size="sm" onClick={() => handleEditSubstitute(absent, period)}>
                                  <Edit size={14} className="me-1" /> Edit
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}

                {substituteSheet.length === 0 && (
                  <div className="text-center py-4 text-muted">No absent teachers for {selectedDate}</div>
                )}
              </Accordion>
            )}
          </Card.Body>
        </Card>

        {/* Alternative Teachers Modal */}
        <Modal show={showAlternativesModal} onHide={() => { setShowAlternativesModal(false); setSelectedPeriodForEdit(null); dispatch(clearAlternativeTeachers()); setSelectedGroup(null); setSelectedTeacherId(""); }} size="md">
          <Modal.Header closeButton><Modal.Title>Select Substitute Teacher</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form>
              <div className="mb-3">
                <Form.Check type="checkbox" id="sameSubjectCheckbox" label="Same Subject Teachers" checked={selectedGroup === "same"} onChange={() => { setSelectedGroup("same"); setSelectedTeacherId(""); }} />
                <Form.Check type="checkbox" id="differentSubjectCheckbox" label="Different Subject Teachers" checked={selectedGroup === "different"} onChange={() => { setSelectedGroup("different"); setSelectedTeacherId(""); }} />
              </div>

              {selectedGroup === "same" && (
                <Form.Group className="mb-3">
                  <Form.Label>Select from Same Subject Teachers</Form.Label>
                  <Form.Select value={selectedTeacherId} onChange={(e) => setSelectedTeacherId(e.target.value)}>
                    <option value="">-- Choose a Teacher --</option>
                    {alternativeTeachers?.sameSubjectTeachers?.map((teacher) => (
                      <option key={teacher.teacher_id} value={teacher.teacher_id}>
                        {teacher.name} ({teacher.qualification})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {selectedGroup === "different" && (
                <Form.Group className="mb-3">
                  <Form.Label>Select from Different Subject Teachers</Form.Label>
                  <Form.Select value={selectedTeacherId} onChange={(e) => setSelectedTeacherId(e.target.value)}>
                    <option value="">-- Choose a Teacher --</option>
                    {alternativeTeachers?.differentSubjectTeachers?.map((teacher) => (
                      <option key={teacher.teacher_id} value={teacher.teacher_id}>
                        {teacher.name} ({teacher.qualification})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowAlternativesModal(false); setSelectedPeriodForEdit(null); dispatch(clearAlternativeTeachers()); setSelectedGroup(null); setSelectedTeacherId(""); }}>Cancel</Button>
            <Button variant="primary" onClick={() => handleUpdateSubstitute(selectedTeacherId)} disabled={!selectedTeacherId}>Assign Selected</Button>
          </Modal.Footer>
        </Modal>

        {/* Mark Absent Modal */}
        <Modal show={showModal} onHide={() => { setShowModal(false); dispatch(clearTeacherPeriods()); setFormData({ teacher_id: [], date: new Date().toISOString().split("T")[0], periods: [] }); }} size="lg">
          <Form onSubmit={handleMarkAbsent}>
            <Modal.Header closeButton><Modal.Title>Mark Teacher Absent</Modal.Title></Modal.Header>

            <Modal.Body>
              <Row>
                <Col md={12}>
                  {/* Teacher checkbox list */}
                  <Form.Group className="mb-3">
                    <Form.Label>Select Teachers</Form.Label>
                    <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "6px", padding: "10px" }}>
                      {teachers && teachers.length > 0 ? teachers.map((teacher) => {
                        const isChecked = formData.teacher_id.includes(String(teacher.teacher_id));
                        return (
                          <div key={teacher.teacher_id} className="d-flex align-items-center mb-2">
                            <Form.Check type="checkbox" checked={isChecked} onChange={(e) => toggleTeacherCheckbox(teacher.teacher_id, e.target.checked)} className="me-2" />
                            <div>
                              <strong>{teacher.name}</strong> <span className="text-muted">({teacher.qualification})</span>
                            </div>
                          </div>
                        );
                      }) : <div className="text-muted small">No teachers found</div>}
                    </div>
                  </Form.Group>

                  {/* Date */}
                  <Form.Group className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control type="date" value={formData.date} onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))} required />
                  </Form.Group>

                  {/* Period selection grouped by teacher */}
                  {formData.teacher_id.length > 0 && (
                    <div className="mb-3">
                      <h6 className="mb-3 fw-bold">Select Periods for Each Absent Teacher</h6>

                      {groupedPeriods.map((teacher) => (
                        <Card key={teacher.teacher_id} className="mb-3">
                          {/* Header row: checkbox left + name, arrow right */}
                          <Card.Header className="d-flex justify-content-between align-items-center px-3" style={{ background: "#f8f9fa" }}>
                            <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="checkbox"
                                checked={
                                  teacher.periods.length > 0 &&
                                  teacher.periods.every(period =>
                                    formData.periods.some(p => String(p.teacher_id) === String(teacher.teacher_id) && String(p.period_id) === String(period.period_id))
                                  )
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const newPeriods = teacher.periods.map(period => ({
                                      teacher_id: teacher.teacher_id,
                                      period_id: period.period_id,
                                      subject_id: period.subject_id,
                                      section_id: period.section_id,
                                    }));
                                    setFormData(prev => ({
                                      ...prev,
                                      periods: [
                                        ...prev.periods.filter(p => String(p.teacher_id) !== String(teacher.teacher_id)),
                                        ...newPeriods,
                                      ],
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      periods: prev.periods.filter(p => String(p.teacher_id) !== String(teacher.teacher_id)),
                                    }));
                                  }
                                }}
                                className="me-2"
                              />
                              <strong style={{ fontSize: "15px" }}>{teacher.teacher_name}</strong>
                            </div>

                            <div style={{ cursor: "pointer" }} onClick={() => setFormData(prev => ({ ...prev, [`show_${teacher.teacher_id}`]: !prev[`show_${teacher.teacher_id}`] }))}>
                              {formData[`show_${teacher.teacher_id}`] ? <span style={{ fontSize: "16px" }}>▲</span> : <span style={{ fontSize: "16px" }}>▼</span>}
                            </div>
                          </Card.Header>

                          {formData[`show_${teacher.teacher_id}`] && (
                            <Card.Body style={{ maxHeight: "240px", overflowY: "auto" }}>
                              {teacher.periods.length > 0 ? teacher.periods.map((period) => {
                                const checked = formData.periods.some(p => String(p.period_id) === String(period.period_id) && String(p.teacher_id) === String(teacher.teacher_id));
                                return (
                                  <div key={period.period_id} className="d-flex align-items-center mb-2 border rounded p-2" style={{ background: "#f9f9f9" }}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFormData(prev => ({
                                            ...prev,
                                            periods: [
                                              ...prev.periods,
                                              {
                                                teacher_id: teacher.teacher_id,
                                                period_id: period.period_id,
                                                subject_id: period.subject_id,
                                                section_id: period.section_id,
                                              },
                                            ],
                                          }));
                                        } else {
                                          setFormData(prev => ({
                                            ...prev,
                                            periods: prev.periods.filter(p => !(String(p.period_id) === String(period.period_id) && String(p.teacher_id) === String(teacher.teacher_id))),
                                          }));
                                        }
                                      }}
                                      className="me-3"
                                    />

                                    <div>
                                      <div className="fw-semibold">
                                        {period.period_name} ({(period.start_time || "").slice(0, 5)} - {(period.end_time || "").slice(0, 5)})
                                      </div>

                                      <div>
                                        <Badge bg="primary" className="me-2">{period.subject_name}</Badge>
                                        <Badge bg="secondary">{period.class_name} {period.section_name}</Badge>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }) : <div className="text-muted small">No periods for this teacher</div>}
                            </Card.Body>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}

                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowModal(false); dispatch(clearTeacherPeriods()); setFormData({ teacher_id: [], date: new Date().toISOString().split("T")[0], periods: [] }); }}>Cancel</Button>
              <Button variant="danger" type="submit" disabled={formData.periods.length === 0 || formData.teacher_id.length === 0}>
                Mark Absent & Auto-Assign
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Print Modal */}
        {showPrintModal && (
          <Modal show={showPrintModal} onHide={() => { setShowPrintModal(false); setPrintTeacher(null); }} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>{printTeacher ? `Printable Sheet - ${printTeacher.teacher_name}` : "Printable Substitute Teacher Report"}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div>
                <div className="d-flex mb-3 justify-content-between">
                  <ReactToPrint
                    documentTitle={printTeacher ? `Substitute_Report_${printTeacher.teacher_name}` : "SubstituteTeacher_Report"}
                    pageStyle={`
                      @media print {
                        @page { size: A4; margin: 0 50px !important; }
                        body { -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
                      }
                    `}
                    trigger={() => (
                      <button className="no_print btn btn-outline-primary mb-3">
                        <i className="fa fa-print" aria-hidden="true"></i> Print
                      </button>
                    )}
                    content={() => printRef.current}
                  />
                  <button className="btn btn-danger" onClick={() => { setShowPrintModal(false); setPrintTeacher(null); }}>Close</button>
                </div>

 <div ref={printRef}>
  <PrintDataTable
    columns={[
      { Header: "Period", accessor: "period_name" },
      { Header: "Time", accessor: "time" },
      { Header: "Section", accessor: "section" },
      { Header: "Absent Teacher", accessor: "absent_teacher_name" },
      { Header: "Teacher Assigned", accessor: "replace_teacher_name" },
      { Header: "Signature", accessor: "signature" },
    ]}
    data={
  printTeacher
    ? [...printTeacher.periods]   // ← copy first!
        .sort(
          (a, b) =>
            new Date(`1970-01-01T${a.start_time}`) -
            new Date(`1970-01-01T${b.start_time}`)
        )
        .map((period) => ({
          period_name: period.period_name,
          time: `${(period.start_time || "").slice(0, 5)} - ${(period.end_time || "").slice(0, 5)}`,
          section: period.section_name,
          absent_teacher_name: printTeacher.teacher_name || "N/A",
          replace_teacher_name: period.substitute_teacher_name || "N/A",
          signature: "",
        }))
    : [...substituteSheet.flatMap((item) =>
        item.periods.map((period) => ({
          period_name: period.period_name,
          start_time: period.start_time,
          time: `${(period.start_time || "").slice(0, 5)} - ${(period.end_time || "").slice(0, 5)}`,
          section: period.section_name,
          absent_teacher_name: item.teacher_name || "N/A",
          replace_teacher_name: period.substitute_teacher_name || "N/A",
          signature: "",
        }))
      )].sort(
        (a, b) =>
          new Date(`1970-01-01T${a.start_time}`) -
          new Date(`1970-01-01T${b.start_time}`)
      )
}

    tableRef={printRef}
  />
</div>


              </div>
            </Modal.Body>
          </Modal>
        )}

      </div>
    </div>
  );
};

export default SubstituteManagement;
